import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatAmount } from '~/helpers/formatters.helper';
import useTranslate from '~/hooks/useTranslate';
import { updateForm } from '~/store/slices/loanOnboardingOffer.slice';
import { RootState } from '~/store/types/sharedTypes';
import { BaseArrangement } from '~/styles/BaseStyledComponents';
import { Button, Stack, Typography } from '@qred/components-library';
import PaymentCalculatorModal from '~/pages/LoanOffer/components/PaymentCalculator/PaymentCalculatorModal';
import useModals from '~/hooks/useModals';
import { pushToGtmOnboardingAction } from '~/store/actions/gtmActions';
import { onChangeInputEvent } from '~/types/types';
import { getDefaultLoanTerm } from '~/helpers/onboarding/LoanTerm.helper';
import { CampaignType } from '~/enums';
import { useOnboardingOfferSelectors } from '~/store/selectors/onboardingOffer.selector';
import PaymentDetails from './PaymentDetails';
import TermsSlider from '../../../LoanOffer/components/TermsSlider';
import AmountSlider from '../../../LoanOffer/components/AmountSlider';
import { LoanAmountDefaultValues } from '~/constants/markets';

const LoanOfferAdjustment = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const modals = useModals();

  const { monthlyFee } = useOnboardingOfferSelectors();

  const {
    onboardingOffer: { overview, form },
    matchedMedia,
  } = useSelector((state: RootState) => state);
  const { market } = useSelector((state: RootState) => state.intl);
  const {
    min: minimumLoanAmount,
    step: stepLoanAmount,
  } = LoanAmountDefaultValues[market];

  const { min: defaultMinLoanTerm, step: stepLoanTerm } = getDefaultLoanTerm(
    market
  );
  let minTerm = defaultMinLoanTerm;
  if (
    overview.campaignType === CampaignType.AmortizationFreeMonths &&
    overview.campaignPrincipalFreeMonths >= minTerm
  ) {
    minTerm = overview.campaignPrincipalFreeMonths + 1;
  }

  const roundedCurrentAmountLeft =
    Math.ceil(overview?.currentLoanAmountLeft / minimumLoanAmount) *
    minimumLoanAmount;

  const roundedOriginalAmount =
    Math.ceil(
      Math.min(overview.amountOriginal, overview.amount) / stepLoanAmount
    ) * stepLoanAmount;

  const sliderMinAmount =
    overview.currentLoanAmountLeft === overview.maxAllowedAmount
      ? overview.maxAllowedAmount
      : roundedCurrentAmountLeft + minimumLoanAmount;

  const sliderDefaultAmount = Math.max(
    sliderMinAmount,
    Math.min(roundedOriginalAmount, overview.maxAllowedAmount)
  );

  useEffect(() => {
    dispatch(
      updateForm({
        amount: form.amount || sliderDefaultAmount,
        loanPeriod: form.loanPeriod || overview.maxAllowedTerm,
      })
    );
  }, [overview.amountOriginal]);

  const onAmountSliderChange = (event: onChangeInputEvent) => {
    const newAmount = Number(event.currentTarget.value);
    dispatch(
      updateForm({
        amount: newAmount,
      })
    );
    dispatch(
      pushToGtmOnboardingAction({
        actionName: 'loan_offer_amount_change',
        stepActionNumber: 0.1,
      })
    );
  };

  const onTermsSliderChange = (event: onChangeInputEvent) => {
    const newTerm = Number(event.currentTarget.value);
    dispatch(
      updateForm({
        loanPeriod: newTerm,
      })
    );
    dispatch(
      pushToGtmOnboardingAction({
        actionName: 'loan_offer_terms_change',
        stepActionNumber: 0.2,
      })
    );
  };

  const handleOpenPaymentScheduleModal = () => {
    dispatch(
      pushToGtmOnboardingAction({
        actionName: 'loan_offer_payment_plan_open',
        stepActionNumber: 0.3,
      })
    );

    modals.openModal({
      header: (
        <Typography weight={600} size="xl">
          {translate('PaymentCalculator.PaymentSchedule')}
        </Typography>
      ),
      children: (
        <PaymentCalculatorModal
          amount={form.amount}
          loanPeriod={form.loanPeriod}
          monthlyFee={monthlyFee}
          campaignType={overview.campaignType}
          campaignDiscount={overview.campaignDiscount}
          campaignPrincipalFreeMonths={overview.campaignPrincipalFreeMonths}
          campaignInterestRateDiscount={overview.campaignInterestRateDiscount}
        />
      ),
      footer: (
        <Stack align="center">
          <Button variant="secondary" size="md" onClick={modals.closeAll}>
            {translate('Ok')}
          </Button>
        </Stack>
      ),
      fullScreen: matchedMedia.smallScreenPortrait,
      fixedHeaderAndFooter: matchedMedia.smallScreenPortrait,
      filledBackground: true,
      size: 'xl',
    });
  };

  return (
    <Stack spacing="xs">
      <BaseArrangement mb={2}>
        <Typography lineHeight="xl">
          {translate('OnboardingOffer.LoanOfferAdjustmentDescription', {
            loanOffer: (
              <Typography color="information.700" weight={600} lineHeight="xl">
                {formatAmount(overview.maxAllowedAmount)}
              </Typography>
            ),
          })}
        </Typography>
      </BaseArrangement>
      <AmountSlider
        amount={form.amount}
        maxAllowedAmount={overview.maxAllowedAmount}
        minAmount={sliderMinAmount}
        sliderStep={stepLoanAmount}
        onAmountSliderChange={onAmountSliderChange}
      />
      <TermsSlider
        loanPeriod={form.loanPeriod}
        minTerm={minTerm}
        maxAllowedTerm={overview.maxAllowedTerm}
        onTermsSliderChange={onTermsSliderChange}
        sliderStep={stepLoanTerm}
      />
      <PaymentDetails />
      <Button
        size="md"
        variant="secondary"
        onClick={() => handleOpenPaymentScheduleModal()}
      >
        {translate('ViewPaymentSchedule')}
      </Button>
    </Stack>
  );
};

export default LoanOfferAdjustment;
